import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import TempleService from "../services/templeService"

const TemplesPage = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedState, setSelectedState] = useState("all")
  const [viewMode, setViewMode] = useState("grid")
  const [filteredTemples, setFilteredTemples] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
 
  // Categories for filtering
  const categories = [
    { id: "", name: "All Categories" },
    { id: "Shiva", name: "Shiva Temples" },
    { id: "Vishnu", name: "Vishnu Temples" },
    { id: "Devi", name: "Devi Temples" },
    { id: "Ganesha", name: "Ganesha Temples" },
    { id: "Other", name: "Other Temples" },
  ]

  // States for filtering
  const states = [
    { id: "all", name: "All States" },
    { id: "Uttarakhand", name: "Uttarakhand" },
    { id: "Jammu & Kashmir", name: "Jammu & Kashmir" },
    { id: "Andhra Pradesh", name: "Andhra Pradesh" },
    { id: "Punjab", name: "Punjab" },
    { id: "Tamil Nadu", name: "Tamil Nadu" },
    { id: "Uttar Pradesh", name: "Uttar Pradesh" },
    { id: "Odisha", name: "Odisha" },
    { id: "Gujarat", name: "Gujarat" },
    { id: "Delhi", name: "Delhi" },
    { id: "Maharashtra", name: "Maharashtra" },
  ]

  // Filter temples based on search, category, and state
  useEffect(() => {
    const fetchTemples = async () => {
      try {
        setLoading(true)

        // Prepare query parameters
        const params = {
          status: "published", // Only show published temples
          search: searchTerm || undefined,
        }

        if (selectedCategory !== "all") {
          params.category = selectedCategory
        }

        if (selectedState !== "all") {
          params.state = selectedState
        }

        // Fetch temples from API
        const response = await TempleService.getTemples(params)

        setFilteredTemples(response.temples || [])
      } catch (err) {
        setError("Failed to fetch temples. Please try again.")
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchTemples()
  }, [searchTerm, selectedCategory, selectedState])

  return (
    <div className="min-h-screen bg-gradient-to-b from-divine-50 via-white to-sacred-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url(https://res.cloudinary.com/dvc6avwrw/image/upload/v1744612314/temple_images/bgtemple_f9aq4k.jpg)" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-royal-900/80 to-royal-800/80"></div>
        <div className="container mx-auto relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Explore Sacred Temples</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Discover the spiritual and architectural marvels that have stood as testaments to India's rich cultural
            heritage
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 px-4 bg-white shadow-md relative z-10 -mt-8 rounded-t-3xl">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="w-full md:w-1/3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search temples, deities, locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-500"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Category Filter */}
            <div className="w-full md:w-1/4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-500"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* State Filter */}
            <div className="w-full md:w-1/4">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-500"
              >
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            {/* View Toggle */}
            <div className="w-full md:w-auto flex justify-center">
              <div className="bg-gray-100 rounded-lg p-1 flex">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-2 rounded-md ${viewMode === "grid" ? "bg-royal-600 text-white" : "text-gray-600"}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Temples Display Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Results Count */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-royal-700">
              {loading
                ? "Loading temples..."
                : error
                  ? "Error loading temples"
                  : `${filteredTemples.length} ${filteredTemples.length === 1 ? "Temple" : "Temples"} Found`}
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-600 mb-2"></div>
              <p className="text-gray-600">Loading temples...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 mx-auto mb-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p>{error}</p>
            </div>
          ) : (
            <>
              {/* Grid View */}
              {viewMode === "grid" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredTemples.map((temple) => (
                    <Link
                      key={temple._id}
                      to={`/temple/${temple._id}`}
                      className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300"
                    >
                      <div className="relative">
                        <img
                          src={temple.mainImage || "/placeholder.svg?height=300&width=400"}
                          alt={temple.name}
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-white/90 text-royal-700 px-2 py-1 rounded-full text-sm font-bold flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-yellow-500 mr-1"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          {temple.popularity || "4.5"}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="text-xl font-semibold text-royal-700 mb-1">{temple.name}</h3>
                        <div className="flex items-center text-gray-600 mb-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {temple.location?.address}, {temple.location?.city}, {temple.location?.state}
                        </div>
                        <p className="text-sacred-600 mb-3">{temple.deity}</p>
                        <p className="text-gray-600 text-sm line-clamp-2">{temple.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="space-y-6">
                  {filteredTemples.map((temple) => (
                    <Link
                      key={temple.id}
                      to={`/temple/${temple.id}`}
                      className="bg-white rounded-lg shadow-lg overflow-hidden block transform hover:scale-102 transition-transform duration-300"
                    >
                      <div className="flex flex-col md:flex-row">
                        <div className="md:w-1/4">
                          <img
                            src={temple.image || "/placeholder.svg?height=300&width=400"}
                            alt={temple.name}
                            className="w-full h-48 md:h-full object-cover"
                          />
                        </div>
                        <div className="md:w-3/4 p-6">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-2xl font-semibold text-royal-700 mb-2">{temple.name}</h3>
                              <div className="flex items-center text-gray-600 mb-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 mr-1"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                {temple.location}
                              </div>
                            </div>
                            <div className="bg-white/90 text-royal-700 px-3 py-1 rounded-full text-sm font-bold flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-yellow-500 mr-1"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              {temple.popularity || "4.5"}
                            </div>
                          </div>
                          <p className="text-sacred-600 mb-3">{temple.deity}</p>
                          <p className="text-gray-600 mb-4">{temple.description}</p>
                          <div className="flex justify-between items-center">
                            <span className="inline-block bg-royal-100 text-royal-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide">
                              {categories.find((cat) => cat.id === temple.category)?.name || temple.category}
                            </span>
                            <div className="text-royal-600 hover:text-royal-800 font-medium flex items-center">
                              View Details
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 ml-1"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* No Results */}
              {filteredTemples.length === 0 && !loading && !error && (
                <div className="text-center py-12">
                  <div className="text-6xl text-gray-300 mb-4">🕉️</div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">No Temples Found</h3>
                  <p className="text-gray-600 mb-6">
                    We couldn't find any temples matching your search criteria. Please try different filters.
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm("")
                      setSelectedCategory("all")
                      setSelectedState("all")
                    }}
                    className="bg-royal-600 hover:bg-royal-700 text-white px-6 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 px-4 bg-gradient-to-r from-royal-700 to-royal-900 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Plan Your Temple Pilgrimage</h2>
          <p className="text-xl max-w-3xl mx-auto mb-8">
            Ready to embark on a spiritual journey? Explore our curated pilgrimage packages and travel guides.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pilgrimage"
              className="bg-white text-royal-700 hover:bg-divine-100 px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              View Pilgrimage Routes
            </Link>
            <Link
              to="/contact"
              className="bg-transparent hover:bg-white/10 text-white border-2 border-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              Contact for Custom Tours
            </Link>
          </div>
        </div>
      </section> */}

      <Footer />
    </div>
  )
}

export default TemplesPage
